import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Loader2, Redo2, Undo2 } from 'lucide-react';
import {
  addAudio,
  appendClip,
  clipAtTime,
  clipGradeOrIdentity,
  compileEditorTimeline,
  deleteClipsForAsset,
  deleteAudio,
  deleteClip,
  deleteText,
  duplicateClip,
  editorStateFromLegacy,
  emptyEditorState,
  formatFromLegacy,
  historyCommit,
  historyInit,
  historyRedo,
  historyUndo,
  parseEditorState,
  projectDuration,
  setClipGrade,
  setClipOffset,
  setClipScale,
  splitClip,
  whiteBalanceForRgb,
  updateBlank,
  updateLogo,
  placeSecondLogo,
  type EditorHistory,
  type EditorState,
} from '@rehub/propertycut-timeline';
import type { AgentData, ListingData, ProjectStatus, PropertyCutAsset, PropertyCutMusicTrack, RenderFormat, StartWorkflow } from '../../types';
import {
  deleteAsset,
  fetchAssets,
  fetchMusicLibrary,
  fetchProject,
  fetchRenders,
  getFreshSignedUrl,
  saveEditorDocument,
  uploadAgentLogo,
  uploadAsset,
} from '../../services/supabase';
import ExportSheet from './ExportSheet';
import PreviewStage, { type PreviewStageHandle } from './PreviewStage';
import SidePanel, { type Tab, type UploadJob } from './SidePanel';
import TimelineView from './TimelineView';
import { classifyUpload, formatLabel, type EditorSelection } from './model';
import { useSceneJobs } from './useSceneJobs';
import { useVideoLengths } from './useVideoLengths';

interface VideoEditorProps {
  userId: string;
  projectId: string;
  credits: number;
  onBack: () => void;
  onCredits: (credits: number) => void;
}

export default function VideoEditor({ userId, projectId, credits, onBack, onCredits }: VideoEditorProps) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [title, setTitle] = useState('Untitled video');
  const [format, setFormat] = useState<RenderFormat>('landscape');
  const [listing, setListing] = useState<ListingData>({});
  const [agent, setAgent] = useState<AgentData>({});
  const [status, setStatus] = useState<ProjectStatus>('draft');
  const [workflow, setWorkflow] = useState<StartWorkflow | null>(null);
  const [assets, setAssets] = useState<PropertyCutAsset[]>([]);
  const [music, setMusic] = useState<PropertyCutMusicTrack[]>([]);
  const [musicUrls, setMusicUrls] = useState<Record<string, string>>({});
  const [history, setHistory] = useState<EditorHistory>(() => historyInit(emptyEditorState()));
  const [preview, setPreview] = useState<EditorState | null>(null);
  const [selected, setSelected] = useState<EditorSelection>(null);
  const [tab, setTab] = useState<Tab>('media');
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [zoom, setZoom] = useState(48);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [dirty, setDirty] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState<UploadJob[]>([]);
  const uploadBatch = useRef(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  // Phones can't show the library and the stage side by side.
  const [mobilePane, setMobilePane] = useState<'edit' | 'preview'>('edit');
  const fileRef = useRef<HTMLInputElement>(null);
  const stageRef = useRef<PreviewStageHandle>(null);
  const [neutralPickClipId, setNeutralPickClipId] = useState<string | null>(null);
  const [gradeNote, setGradeNote] = useState<string | null>(null);
  const voiceRef = useRef<HTMLInputElement>(null);
  const ready = useRef(false);
  const previewRef = useRef<EditorState | null>(null);
  const snapshot = useRef({ title, listing, agent, format, status, history });

  const state = preview ?? history.present;
  snapshot.current = { title, listing, agent, format, status, history: { ...history, present: state } };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [project, rows, tracks, renders] = await Promise.all([
          fetchProject(projectId),
          fetchAssets(projectId),
          fetchMusicLibrary(),
          fetchRenders(projectId),
        ]);
        if (cancelled) return;
        if (!project) {
          setLoadError('Project not found.');
          return;
        }
        const freshAssets = await Promise.all(rows.map(async (asset) => ({
          ...asset,
          signed_url: await getFreshSignedUrl(asset.storage_path, asset.signed_url),
        })));
        const urls: Record<string, string> = {};
        await Promise.all(tracks.map(async (track) => {
          const url = await getFreshSignedUrl(track.storage_path, null);
          if (url) urls[track.id] = url;
        }));
        const bits = {
          transitionStyle: project.transition_style,
          listing: project.listing_data,
          agent: project.agent_data,
          outputFormat: project.output_format,
          templateId: project.template_id,
        };
        const next = parseEditorState(project.editor_state) ?? editorStateFromLegacy(freshAssets, bits);
        setTitle(project.title || 'Untitled video');
        setFormat(formatFromLegacy(bits));
        setListing(project.listing_data ?? {});
        setAgent(project.agent_data ?? {});
        setStatus(project.status);
        setWorkflow(project.start_workflow ?? null);
        setAssets(freshAssets);
        setMusic(tracks);
        setMusicUrls(urls);
        setHistory(historyInit(next));
        const live = renders.find((row) => row.status === 'queued' || row.status === 'processing');
        if (live) setExportOpen(true);
      } catch (err: any) {
        if (!cancelled) setLoadError(err?.message || 'Could not open this project.');
      } finally {
        if (!cancelled) {
          ready.current = true;
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const compiled = useMemo(() => compileEditorTimeline({
    state,
    format,
    media: assets.map((asset) => ({
      id: asset.id,
      url: asset.signed_url || '',
      kind: asset.kind,
      durationSeconds: asset.duration_seconds,
      name: asset.original_filename,
    })),
    musicUrls,
    logoUrl: agent.logo_url,
    logoUrl2: agent.logo_url_2,
  }), [agent.logo_url, agent.logo_url_2, assets, format, musicUrls, state]);

  const issues = compiled.issues;

  const duration = Math.max(projectDuration(state), compiled.ir.totalLength);
  const stopAt = useRef<number | null>(null);
  useEffect(() => {
    if (!playing) stopAt.current = null;
  }, [playing]);

  useEffect(() => {
    if (!neutralPickClipId) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNeutralPickClipId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [neutralPickClipId]);

  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      setTime((current) => {
        const next = current + dt;
        const limit = Math.min(duration, stopAt.current ?? Infinity);
        if (next >= limit) {
          setPlaying(false);
          return limit;
        }
        return next;
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [duration, playing]);

  const persist = useCallback(async () => {
    const current = snapshot.current;
    setSaveStatus('saving');
    try {
      await saveEditorDocument({
        projectId,
        editorState: current.history.present,
        outputFormat: current.format,
        title: current.title,
        listing: current.listing,
        agent: current.agent,
        status: current.status,
      });
      setStatus((value) => (value === 'draft' ? 'editing' : value));
      setSaveStatus('saved');
      setDirty(false);
    } catch (err) {
      setSaveStatus('error');
      throw err;
    }
  }, [projectId]);

  useEffect(() => {
    if (!ready.current || !dirty) return;
    const timer = window.setTimeout(() => {
      void persist().catch(() => undefined);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [dirty, history.present, title, listing, agent, persist]);

  const edit = useCallback((recipe: (current: EditorState) => EditorState) => {
    setPreview(null);
    previewRef.current = null;
    setHistory((current) => historyCommit(current, recipe(current.present)));
    setDirty(true);
  }, []);

  const applyLive = useCallback((recipe: (current: EditorState) => EditorState, commit: boolean) => {
    setPlaying(false);
    const base = previewRef.current ?? snapshot.current.history.present;
    const next = recipe(base);
    if (!commit) {
      previewRef.current = next;
      setPreview(next);
      return;
    }
    previewRef.current = null;
    setPreview(null);
    setHistory((current) => historyCommit(current, next));
    setDirty(true);
  }, []);

  const historyRef = useRef(history);
  historyRef.current = history;
  const sync = useCallback((recipe: (current: EditorState) => EditorState) => {
    if (!ready.current) return;
    const base = historyRef.current.present;
    if (recipe(base) === base) return;
    setHistory((current) => ({ ...current, present: recipe(current.present) }));
    setDirty(true);
  }, []);

  const scenes = useSceneJobs({
    userId,
    projectId,
    format,
    state,
    assets,
    setAssets,
    edit,
    sync,
    onCredits,
  });
  useVideoLengths({ assets, setAssets, sync });

  const undo = useCallback(() => {
    setHistory((current) => historyUndo(current));
    setDirty(true);
  }, []);
  const redo = useCallback(() => {
    setHistory((current) => historyRedo(current));
    setDirty(true);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA';
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
        return;
      }
      if (typing) return;
      if (event.key === ' ') {
        event.preventDefault();
        setPlaying((value) => !value);
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'd' && selected?.type === 'clip') {
        event.preventDefault();
        edit((current) => duplicateClip(current, selected.id));
      }
      if ((event.key === 'Backspace' || event.key === 'Delete') && selected) {
        event.preventDefault();
        edit((current) => {
          if (selected.type === 'clip') return deleteClip(current, selected.id);
          if (selected.type === 'text') return deleteText(current, selected.id);
          if (selected.type === 'logo') return updateLogo(current, { visible: false });
          return deleteAudio(current, selected.id);
        });
        setSelected(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [edit, redo, selected, undo]);

  const ingest = async (files: File[], as: 'auto' | 'voiceover' = 'auto') => {
    if (!files.length || uploading) return;
    const batch = uploadBatch.current + 1;
    uploadBatch.current = batch;
    const jobs: UploadJob[] = files.map((file, index) => ({
      id: `${batch}-${index}-${file.name}`,
      name: file.name,
      status: 'waiting',
      fraction: 0,
    }));
    setUploads(jobs);
    setUploadError(null);
    setUploading(true);
    let failed = false;
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      const jobId = jobs[index].id;
      const kind = classifyUpload(file);
      if (as === 'voiceover' && kind !== 'audio') {
        failed = true;
        setUploadError(`${file.name} isn't an audio file. Use MP3, WAV, M4A or AAC for a voiceover.`);
        setUploads((current) => current.map((job) => (
          job.id === jobId ? { ...job, status: 'error' } : job
        )));
        continue;
      }
      if (!kind) {
        failed = true;
        setUploadError(`Couldn't use ${file.name}. Use a photo, video, or audio file.`);
        setUploads((current) => current.map((job) => (
          job.id === jobId ? { ...job, status: 'error' } : job
        )));
        continue;
      }
      setUploads((current) => current.map((job) => (
        job.id === jobId ? { ...job, status: 'uploading', fraction: 0 } : job
      )));
      try {
        const asset = await uploadAsset({
          userId,
          projectId,
          file,
          kind,
          onProgress: (fraction) => {
            setUploads((current) => current.map((job) => (
              job.id === jobId && job.status === 'uploading' ? { ...job, fraction } : job
            )));
          },
        });
        setAssets((current) => [...current, asset]);
        edit((current) => (
          kind === 'audio'
            ? addAudio(current, {
                assetId: asset.id,
                durationSeconds: asset.duration_seconds,
                role: as === 'voiceover' ? 'voiceover' : 'music',
              })
            : appendClip(current, { id: asset.id, kind, durationSeconds: asset.duration_seconds })
        ));
        setUploads((current) => current.map((job) => (
          job.id === jobId ? { ...job, status: 'done', fraction: 1 } : job
        )));
      } catch (err: any) {
        failed = true;
        setUploadError(`${file.name}: ${err?.message || 'Upload failed.'}`);
        setUploads((current) => current.map((job) => (
          job.id === jobId ? { ...job, status: 'error' } : job
        )));
      }
    }
    setUploading(false);
    if (!failed) {
      window.setTimeout(() => {
        if (uploadBatch.current === batch) setUploads([]);
      }, 1600);
    }
  };

  const addExisting = (asset: PropertyCutAsset) => {
    edit((current) => (
      asset.kind === 'audio'
        ? addAudio(current, { assetId: asset.id, durationSeconds: asset.duration_seconds })
        : appendClip(current, { id: asset.id, kind: asset.kind, durationSeconds: asset.duration_seconds })
    ));
  };

  const removeAsset = async (asset: PropertyCutAsset) => {
    const previous = assets;
    setAssets((current) => current.filter((item) => item.id !== asset.id));
    edit((current) => deleteClipsForAsset(current, asset.id));
    try {
      await deleteAsset(asset.id);
    } catch (err: any) {
      setAssets(previous);
      setUploadError(`${asset.original_filename || 'File'}: ${err?.message || 'Could not remove it.'}`);
    }
  };

  const uploadLogo = async (file: File, slot: 'primary' | 'second' = 'primary') => {
    try {
      const url = await uploadAgentLogo(userId, file);
      if (slot === 'second') {
        setAgent((current) => ({ ...current, logo_url_2: url }));
        edit(placeSecondLogo);
      } else {
        setAgent((current) => ({ ...current, logo_url: url }));
        setDirty(true);
      }
      setUploadError(null);
    } catch (err: any) {
      const message = `${file.name}: ${err?.message || 'Could not upload the logo.'}`;
      setUploadError(message);
      throw new Error(message);
    }
  };

  const blocked = state.clips.length === 0
    ? 'Add at least one photo or clip'
    : issues[0]?.message ?? null;

  if (loading) {
    return (
      <div className="h-dvh grid place-items-center bg-slate-950 text-white">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="h-dvh grid place-items-center bg-slate-950 text-white px-6">
        <div className="text-center space-y-3">
          <p className="text-sm text-red-300">{loadError}</p>
          <button type="button" onClick={onBack} className="text-sm text-teal-300">Back to projects</button>
        </div>
      </div>
    );
  }

  return (
    <div className="propertycut-editor h-dvh max-h-dvh w-full max-w-[100vw] overflow-hidden flex flex-col bg-slate-950 text-white pb-[env(safe-area-inset-bottom)]">
      <header className="h-14 shrink-0 border-b border-slate-800 px-2 sm:px-3 flex items-center gap-1 sm:gap-3 min-w-0">
        <button type="button" onClick={onBack} className="p-2 shrink-0 text-slate-400 hover:text-white" aria-label="Back to projects">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="min-w-0 flex-1">
          <input
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setDirty(true);
            }}
            className="w-full bg-transparent text-base md:text-sm font-medium min-w-0 border-b border-transparent focus:border-slate-600 outline-none"
            aria-label="Project name"
          />
        </div>
        <span className="text-[11px] text-slate-500 hidden sm:inline shrink-0">{formatLabel(format)}</span>
        <span className={`text-[11px] shrink-0 ${saveStatus === 'error' ? 'text-amber-300' : 'text-slate-500 hidden min-[420px]:inline'}`}>
          {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'error' ? 'Couldn’t save' : 'Saved'}
        </span>
        {uploads.length > 0 && (
          <span className="text-[11px] text-teal-200 shrink-0 hidden sm:inline">
            Uploading {Math.min(uploads.filter((job) => job.status === 'done').length + (uploading ? 1 : 0), uploads.length)} of {uploads.length}
          </span>
        )}
        <button type="button" onClick={undo} disabled={history.past.length === 0} className="p-2 shrink-0 text-slate-300 disabled:opacity-30" aria-label="Undo">
          <Undo2 className="w-4 h-4" />
        </button>
        <button type="button" onClick={redo} disabled={history.future.length === 0} className="p-2 shrink-0 text-slate-300 disabled:opacity-30" aria-label="Redo">
          <Redo2 className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {blocked && <span className="text-[11px] text-amber-200/90 hidden md:inline">{blocked}</span>}
          <button
            type="button"
            disabled={Boolean(blocked)}
            onClick={() => setExportOpen(true)}
            className="rounded-lg bg-teal-400 text-slate-950 text-sm font-semibold px-2.5 sm:px-3 py-2 whitespace-nowrap disabled:bg-slate-800 disabled:text-slate-500"
          >
            <span className="sm:hidden">Export</span>
            <span className="hidden sm:inline">Export video</span>
          </button>
        </div>
      </header>
      {uploads.length > 0 && (
        <div className="h-1 shrink-0 bg-slate-800" aria-hidden>
          <div
            className="h-full bg-teal-400 transition-[width] duration-150"
            style={{
              width: `${Math.round((uploads.reduce((sum, job) => sum + (job.status === 'done' ? 1 : job.fraction), 0) / uploads.length) * 100)}%`,
            }}
          />
        </div>
      )}

      <div className="md:hidden shrink-0 grid grid-cols-2 gap-1 border-b border-slate-800 p-1.5">
        <button
          type="button"
          onClick={() => setMobilePane('edit')}
          className={`rounded-md py-2 text-xs font-semibold ${mobilePane === 'edit' ? 'bg-slate-800 text-white' : 'text-slate-400'}`}
          aria-pressed={mobilePane === 'edit'}
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => setMobilePane('preview')}
          className={`rounded-md py-2 text-xs font-semibold ${mobilePane === 'preview' ? 'bg-slate-800 text-white' : 'text-slate-400'}`}
          aria-pressed={mobilePane === 'preview'}
        >
          Preview
        </button>
      </div>

      <div className="flex-1 min-h-0 min-w-0 flex flex-col md:flex-row">
        <SidePanel
          mobileVisible={mobilePane === 'edit'}
          tab={tab}
          onTab={setTab}
          state={state}
          assets={assets}
          music={music}
          musicPreviewUrls={musicUrls}
          listing={listing}
          projectId={projectId}
          agent={agent}
          selection={selected}
          uploadError={uploadError}
          uploading={uploading}
          uploads={uploads}
          onBrowse={() => fileRef.current?.click()}
          onBrowseVoiceover={() => voiceRef.current?.click()}
          onVoiceoverReady={(asset) => {
            setAssets((current) => [...current, asset]);
            edit((current) => addAudio(current, {
              assetId: asset.id,
              durationSeconds: asset.duration_seconds,
              role: 'voiceover',
            }));
          }}
          onDropFiles={(files) => void ingest(files)}
          onAddAsset={addExisting}
          onRemoveAsset={(asset) => void removeAsset(asset)}
          onEdit={edit}
          onLiveEdit={applyLive}
          onSampleClip={async (clipId) => {
            const scene = compiled.ir.scenes.find((item) => item.id === clipId);
            if (scene && (time < scene.start || time >= scene.start + scene.length - 0.05)) {
              setPlaying(false);
              setTime(Math.min(scene.start + 0.15, scene.start + Math.max(0, scene.length - 0.05)));
            }
            return (await stageRef.current?.sampleClip(clipId)) ?? 'missing';
          }}
          neutralPickClipId={neutralPickClipId}
          onNeutralPick={(clipId) => {
            setGradeNote(null);
            if (!clipId) {
              setNeutralPickClipId(null);
              return;
            }
            const scene = compiled.ir.scenes.find((item) => item.id === clipId);
            setPlaying(false);
            if (scene) setTime(Math.min(scene.start + 0.15, scene.start + Math.max(0, scene.length - 0.05)));
            setMobilePane('preview');
            setNeutralPickClipId(clipId);
          }}
          gradeNote={gradeNote}
          onGradeNote={setGradeNote}
          onListing={(next) => {
            setListing(next);
            setDirty(true);
          }}
          onAgent={(next) => {
            setAgent(next);
            setDirty(true);
          }}
          onUploadLogo={(file) => uploadLogo(file)}
          onUploadSecondLogo={(file) => uploadLogo(file, 'second')}
          onSelect={setSelected}
          playhead={time}
          onPlayFrom={(at, until) => {
            setMobilePane('preview');
            stopAt.current = until ?? null;
            setTime(Math.max(0, at));
            setPlaying(true);
          }}
          scenes={scenes}
          workflow={workflow}
          userId={userId}
        />
        <div className={`${mobilePane === 'preview' ? 'flex' : 'hidden'} md:flex flex-1 min-w-0 min-h-0 flex-col overflow-hidden`}>
          <PreviewStage
            ref={stageRef}
            ir={compiled.ir}
            format={format}
            empty={state.clips.length === 0}
            time={time}
            duration={duration}
            playing={playing}
            issues={issues.map((issue) => issue.message)}
            onTogglePlay={() => setPlaying((value) => !value)}
            onSeek={(next) => {
              setPlaying(false);
              setTime(next);
            }}
            onBrowse={() => fileRef.current?.click()}
            onDropFiles={(files) => void ingest(files)}
            onMediaError={(name) => setUploadError(`Couldn't play ${name}.`)}
            nameForUrl={(url) => assets.find((asset) => asset.signed_url === url)?.original_filename || 'that file'}
            selectedPlateId={selected?.type === 'clip' ? selected.id : null}
            overlaySelected={selected?.type === 'logo'}
            onSelectPlate={(clipId) => {
              setPlaying(false);
              setSelected({ type: 'clip', id: clipId });
            }}
            onSelectOverlay={() => {
              setPlaying(false);
              setSelected({ type: 'logo' });
              setTab('logo');
            }}
            onPlateLogoScale={(clipId, scale, commit, slot) => {
              applyLive((current) => updateBlank(
                current,
                clipId,
                slot === 'second' ? { logo2Scale: scale } : { logoScale: scale },
              ), commit);
            }}
            onOverlayLogoScale={(scale, commit) => {
              applyLive((current) => updateLogo(current, { scale }), commit);
            }}
            onClipScale={(clipId, scale, commit) => {
              applyLive((current) => setClipScale(current, clipId, scale), commit);
            }}
            onClipMove={(clipId, offsetX, offsetY, commit) => {
              applyLive((current) => setClipOffset(current, clipId, offsetX, offsetY), commit);
            }}
            neutralPickClipId={neutralPickClipId}
            onPickedNeutral={(clipId, r, g, b) => {
              const balance = whiteBalanceForRgb(r, g, b);
              edit((current) => {
                const clip = current.clips.find((item) => item.id === clipId);
                return setClipGrade(current, clipId, { ...clipGradeOrIdentity(clip?.grade), ...balance });
              });
              setNeutralPickClipId(null);
              setGradeNote(null);
            }}
            onPickBlocked={() => {
              setNeutralPickClipId(null);
              setGradeNote('Use the sliders — this clip can’t be read from the preview.');
            }}
          />
          <TimelineView
            state={state}
            assets={assets}
            time={time}
            duration={duration}
            zoom={zoom}
            selected={selected}
            onSelect={setSelected}
            onSeek={(next) => {
              setPlaying(false);
              setTime(Math.max(0, next));
            }}
            onZoom={setZoom}
            onPreview={(next) => {
              previewRef.current = next;
              setPreview(next);
            }}
            onCommit={() => {
              const next = previewRef.current;
              previewRef.current = null;
              setPreview(null);
              if (!next) return;
              setHistory((current) => historyCommit(current, next));
              setDirty(true);
            }}
            onSplit={() => {
              const clip = (selected?.type === 'clip'
                ? state.clips.find((item) => item.id === selected.id)
                : null) ?? clipAtTime(state, time);
              if (!clip) return;
              edit((current) => splitClip(current, clip.id, time));
            }}
            onDuplicate={() => {
              if (selected?.type !== 'clip') return;
              edit((current) => duplicateClip(current, selected.id));
            }}
            onDelete={() => {
              if (!selected) return;
              edit((current) => {
                if (selected.type === 'clip') return deleteClip(current, selected.id);
                if (selected.type === 'text') return deleteText(current, selected.id);
                if (selected.type === 'logo') return updateLogo(current, { visible: false });
                return deleteAudio(current, selected.id);
              });
              setSelected(null);
            }}
            onEdit={edit}
          />
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*,audio/*"
        multiple
        hidden
        onChange={(event) => {
          const files = event.target.files ? Array.from(event.target.files) : [];
          event.target.value = '';
          if (files.length) void ingest(files);
        }}
      />

      <input
        ref={voiceRef}
        type="file"
        accept="audio/*"
        hidden
        onChange={(event) => {
          const files = event.target.files ? Array.from(event.target.files) : [];
          event.target.value = '';
          if (files.length) void ingest(files, 'voiceover');
        }}
      />

      <style>{`
        @media (max-width: 767px) {
          .propertycut-editor input:not([type="range"]):not([type="color"]),
          .propertycut-editor textarea,
          .propertycut-editor select {
            font-size: 16px;
          }
        }
      `}</style>

      {exportOpen && (
        <ExportSheet
          projectId={projectId}
          userId={userId}
          format={format}
          credits={credits}
          blockedReason={blocked}
          onBeforeExport={persist}
          onCredits={onCredits}
          onClose={() => setExportOpen(false)}
        />
      )}
    </div>
  );
}
